import React from 'react';
import { ContentType } from 'types/publishedAssets';
import { ContentContainer } from 'components/Content/Atoms/ContentContainer';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { Flex } from 'components/Atoms/Flex/Flex';
import { Spacer } from 'components/Atoms/Spacer/Spacer';
import { ImageBoss } from 'components/Content/Atoms/ImageBoss';
import { RenderMarkdownMarked } from 'components/Atoms/RenderMarkdownMarked/RenderMarkdownMarked';
import { BlockHeading } from 'components/Content/Atoms/BlockHeading';
import styles from './ProfileCards.module.scss';

interface Props {
  content: ContentType;
}

const getInitials = (name: string) => {
  if (!name || typeof name !== 'string' || name.trim() === '') {
    return '?';
  }
  return name
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 3);
};

export function ProfileCards(props: Props) {
  const { content } = props;
  const { getMediaItemFromComponent, getImageCropFromComponent, translate } = useGetContentTranslations();
  const title = translate(content.translations, 'title');
  const description = translate(content.translations, 'description');
  const isOrganizationStyle = (content.metadata as Record<string, string>)?.card_style === 'basic_content';
  const cardStyle = isOrganizationStyle ? 'organization' : 'profile';
  return (
    <ContentContainer content={content}>
      <div>
        <div className={`${styles.container} ${styles[cardStyle] || ''}`}>
          <BlockHeading
            title={title}
            id={content.id}
            // Profile Cards keeps its markdown rendering; the slot preserves the shared grouping.
            secondaryTextSlot={description ? <RenderMarkdownMarked text={description} /> : undefined}
          />
          {content.components.map((component) => {
            const mediaItem = getMediaItemFromComponent(component);
            const image = mediaItem?.url;
            const name = translate(component.translations!, 'name');
            const subtitle =
              translate(component.translations!, 'subtitle') || translate(component.translations!, 'job_title');
            const bodyText = translate(component.translations!, 'body_text');
            const altText = translate(component.translations!, 'alt_text') || name;
            const isOrganization = isOrganizationStyle || component.content_component_type === 'organization';
            if (!name) return null;
            if (isOrganization) {
              return (
                <React.Fragment key={component.id}>
                  <Spacer orientation="vertical" size="small" />
                  <Flex flexDirection="column" className={styles.orgCard}>
                    {image && <ImageBoss url={image} alt={altText} height={64} className={styles.orgLogo} />}
                    {!image && <div className={styles.photoPlaceholder}>{getInitials(name)}</div>}
                    <Flex flexDirection="column">
                      <div className={styles.orgName}>{name}</div>
                      {subtitle && <div className={styles.orgSubtitle}>{subtitle}</div>}
                      {bodyText && <RenderMarkdownMarked text={bodyText} className={styles.orgBodyText} />}
                    </Flex>
                  </Flex>
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={component.id}>
                <Spacer orientation="vertical" size="small" />
                <Flex flexDirection="row" alignItems="center" gap={16}>
                  <>
                    {image && (
                      <ImageBoss
                        url={image}
                        alt={altText}
                        width={86}
                        className={styles.photo}
                        imageCrop={getImageCropFromComponent(component)}
                      />
                    )}
                    {!image && <div className={styles.photoPlaceholder}>{getInitials(name)}</div>}
                    <Flex flexDirection="column">
                      <div className={styles.name}>{name}</div>
                      {subtitle && <div>{subtitle}</div>}
                      {bodyText && <RenderMarkdownMarked text={bodyText} />}
                    </Flex>
                  </>
                </Flex>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </ContentContainer>
  );
}
